import { TraceType } from '../../common/enums/TraceType';
import { InvalidUrlError } from '../../common/errors/errors';
import { BaseGQLService } from '../../common/services/BaseGQLService';
import { FilterOperator, SorterSet } from '../../common/types/gql-types';
import { CursorInfo, PaginationResult } from '../../common/types/pagination';
import { ModelType } from '../../common/utils/GQLIdEncoder';
import { ProjectExceptionType } from '../enums/ProjectExceptionType';
import { ProjectExceptionMapper } from '../mappers/ProjectExceptionMapper';
import { ProjectException } from '../models/ProjectException';

const EXCEPTION_FRAGMENT = `
id
type
term
description
active
traceType
`;

export const CREATE_PROJECT_EXCEPTION_QUERY = `
mutation($projectId: ID!, $type: ProjectExceptionType!, $term: String!, $description: String, $active: Boolean, $traceType: TraceType!){
  createProjectException(
    input: {
      projectId: $projectId,
      type: $type,
      term: $term,
      description: $description,
      active: $active,
      traceType: $traceType
    }
  ) {
    exception {
      ${EXCEPTION_FRAGMENT}
    }
  }
}
`;

export const GET_PROJECT_EXCEPTIONS_QUERY = `
query (
  $projectId: ID!,
  $first: Int,
  $last: Int,
  $after: String,
  $before: String,
  $filters: ProjectExceptionsFilter,
  $sorters: [ProjectExceptionsSorter],
) {
  project: node(id: $projectId) {
    ... on Project {
      exceptions(
        first: $first
        last: $last
        after: $after
        before: $before
        filters: $filters
        sorters: $sorters
      ) {
        totalCount
        pageInfo {
          startCursor
          endCursor
          hasNextPage
          hasPreviousPage
        }
        edges {
          node {
            ${EXCEPTION_FRAGMENT}
          }
        }
      }
    }
  }
}
`;

export const TOGGLE_PROJECT_EXCEPTION_QUERY = `
mutation($exceptionId: ID!, $active: Boolean!){
  updateProjectException(input: { exceptionId: $exceptionId, active: $active}) {
    exception {
      ${EXCEPTION_FRAGMENT}
    }
  }
}
`;

export const DELETE_PROJECT_EXCEPTION_QUERY = `
mutation($exceptionId: ID!){
  deleteProjectException(input: { exceptionId: $exceptionId }) {
    __typename
  }
}
`;

export class ProjectExceptionRepository extends BaseGQLService {
  private readonly exceptionMapper = new ProjectExceptionMapper();

  async getProjectExceptions(
    projectId: number,
    cursor: CursorInfo,
    filters: FilterOperator | null,
    sorters: SorterSet[]
  ): Promise<PaginationResult<ProjectException>> {
    return this.query(
      GET_PROJECT_EXCEPTIONS_QUERY,
      {
        ...this.mapCursorInfo(cursor),
        projectId: this.gqlIdEncoder.encode(ModelType.Project, projectId),
        filters,
        sorters: this.getSorters(sorters),
      },
      (data) => {
        if (!data.project) {
          throw new InvalidUrlError(`Invalid request: could not find Project ${projectId}`);
        }
        const { edges, pageInfo, totalCount } = data.project.exceptions;
        return {
          data: edges.map(({ node }: any) => this.exceptionMapper.mapGQLModel(node, projectId)),
          totalCount,
          pageInfo,
        };
      }
    );
  }

  async createException(
    projectId: number,
    type: ProjectExceptionType,
    term: string,
    description: string | null,
    active: boolean,
    traceType: TraceType
  ): Promise<ProjectException> {
    return this.mutate(
      CREATE_PROJECT_EXCEPTION_QUERY,
      {
        projectId: this.gqlIdEncoder.encode(ModelType.Project, projectId),
        type,
        term,
        description,
        active,
        traceType,
      },
      (data) => this.exceptionMapper.mapGQLModel(data.createProjectException.exception, projectId)
    );
  }

  async toggleException(projectId: number, exceptionId: number, active: boolean): Promise<ProjectException> {
    return this.mutate(
      TOGGLE_PROJECT_EXCEPTION_QUERY,
      {
        exceptionId: this.gqlIdEncoder.encode(ModelType.ProjectException, exceptionId),
        active,
      },
      (data) => this.exceptionMapper.mapGQLModel(data.updateProjectException.exception, projectId)
    );
  }

  async deleteException(exceptionId: number): Promise<void> {
    return this.mutate(DELETE_PROJECT_EXCEPTION_QUERY, {
      exceptionId: this.gqlIdEncoder.encode(ModelType.ProjectException, exceptionId),
    });
  }

  /**
   * Exceptions are returned by the API sorted by ascending ID (oldest-newest).
   * Unless explicit sorters are requested, sort exceptions from newest to oldest.
   *
   * @param sorters the requested sorters
   * @returns the requested sorters if any, otherwise the default sorter
   */
  private getSorters(sorters: SorterSet[]): SorterSet[] {
    if (sorters.length > 0) {
      return sorters;
    }
    return [{ field: 'id', order: 'DESC' }];
  }
}
